import {
  differenceInCalendarDays,
  endOfDay,
  format,
  isEqual,
  isToday,
  isValid,
  isYesterday,
  parse,
  startOfDay,
  subDays,
  subYears,
} from "date-fns";

import { dateRangeOptions } from "components/filters/date-range-filter/date-range-filter";
import {
  CustomDateRangeFilterId,
  DateFilterId,
  ICustomDateFilter,
  IDateFilter,
  IDateRangeFilter,
  IDateRangeWithCompareFilter,
  IFilters,
} from "constants/filters";
import { customPeriodRegex, dateRegex } from "helpers/regexp";

import { IKeyValue } from "./url";

export function getCustomDate(date: Date): DateFilterId {
  let id;
  if (isToday(date)) {
    id = DateFilterId.Today;
  } else if (isYesterday(date)) {
    id = DateFilterId.Yesterday;
  } else if (isToday(subDays(date, -2))) {
    id = DateFilterId.TwoDaysAgo;
  } else if (isToday(subDays(date, -3))) {
    id = DateFilterId.ThreeDaysAgo;
  } else {
    id = DateFilterId.CustomDate;
  }
  return id;
}

export function getCustomCompareDateRange(
  from: Date | string,
  to: Date | string,
  compareFrom: Date | string,
  comapreTo: Date | string
): CustomDateRangeFilterId {
  const daysDiff = Math.abs(differenceInCalendarDays(from, to)) + 1;

  let id;
  if (isEqual(subDays(from, daysDiff), compareFrom) && isEqual(subDays(to, daysDiff), comapreTo)) {
    id = CustomDateRangeFilterId.SamePeriod;
  } else if (isEqual(subYears(from, 1), compareFrom) && isEqual(subYears(to, 1), comapreTo)) {
    id = CustomDateRangeFilterId.SamePeriodInLastYear;
  } else {
    id = CustomDateRangeFilterId.CustomPeriod;
  }
  return id;
}

export function deserializeDateRangeURLParams(dateFrom: string, dateTo: string): IDateRangeFilter | null {
  if (!dateFrom && !dateTo) {
    return null;
  }

  if (dateRegex.test(dateFrom) && dateRegex.test(dateTo)) {
    const from = startOfDay(parse(dateFrom));
    const to = endOfDay(parse(dateTo));
    if (!isValid(from) || !isValid(to) || from > to) {
      return null;
    }
    return {
      from,
      to,
      id: CustomDateRangeFilterId.CustomPeriod,
    };
  }

  return {
    from: dateFrom,
    to: dateTo,
    id: getCustomDateRangeFilterId(dateFrom, dateTo),
  };
}

export function getCustomDateRangeFilterId(from: string, to: string): CustomDateRangeFilterId {
  if (typeof from !== "string" && typeof to !== "string") {
    return CustomDateRangeFilterId.CustomPeriod;
  }

  const definedDateRange = dateRangeOptions.find(
    option => option.relativeValue && option.relativeValue.from === from && option.relativeValue.to === to
  );
  const customTimePeriod = customPeriodRegex.test(from);
  if (definedDateRange) {
    return definedDateRange.id as CustomDateRangeFilterId;
  }
  if (customTimePeriod) {
    return CustomDateRangeFilterId.CustomTime;
  }

  return CustomDateRangeFilterId.CustomPeriod;
}

export function deserializeCustomDateRangeURLParams(
  dateFrom: string,
  dateTo: string,
  compareDateFrom?: string,
  compareDateTo?: string
): IDateRangeWithCompareFilter {
  const dateRangeUrlParams = deserializeDateRangeURLParams(dateFrom, dateTo);
  if (!dateRangeUrlParams) {
    // @ts-expect-error TS2322
    return null;
  }

  // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
  const compareDateRangeUrlParams = deserializeDateRangeURLParams(compareDateFrom, compareDateTo);
  const compare = compareDateRangeUrlParams && {
    ...compareDateRangeUrlParams,
    id: getCustomCompareDateRange(
      // @ts-expect-error TS2345
      dateRangeUrlParams.from,
      dateRangeUrlParams.to,
      compareDateRangeUrlParams.from,
      compareDateRangeUrlParams.to
    ),
  };

  const { from, to, id } = dateRangeUrlParams;
  return {
    from,
    to,
    id,
    ...(compare && { compare: compare || null }),
  };
}

export function deserializeDateURLParams(dateString: string): IDateFilter {
  if (!dateString) {
    // @ts-expect-error TS2322
    return null;
  }

  const date = parse(dateString);
  if (!isValid(date)) {
    // @ts-expect-error TS2322
    return null;
  }

  return { date };
}

export function deserializeCustomDateURLParams(dateString: string): ICustomDateFilter {
  const dateUrlParams = deserializeDateURLParams(dateString);
  if (!dateUrlParams) {
    // @ts-expect-error TS2322
    return null;
  }

  const { date } = dateUrlParams;
  return {
    date,
    id: getCustomDate(date),
  };
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isDateRangeWithCompare(filterValue: any): filterValue is IDateRangeWithCompareFilter {
  return !!filterValue.compare;
}

export interface IDateRangeQueryString {
  date_from?: string;
  date_to?: string;
  compare_date_from?: string;
  compare_date_to?: string;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function formatIfDate(date: string | Date) {
  if (typeof date === "string") {
    return date;
  }
  return format(date, "YYYY-MM-DD");
}

export function mapDateRangeToQueryString(filterValue: IDateRangeFilter): IDateRangeQueryString {
  if (filterValue == null || typeof filterValue !== "object") {
    return {};
  }

  return {
    ...(filterValue.from && {
      date_from: formatIfDate(filterValue.from),
    }),
    ...(filterValue.to && {
      date_to: formatIfDate(filterValue.to),
    }),
    ...(isDateRangeWithCompare(filterValue) && {
      // @ts-expect-error TS18048
      ...(filterValue.compare.from && {
        // @ts-expect-error TS18048
        compare_date_from: formatIfDate(filterValue.compare.from),
      }),
      // @ts-expect-error TS18048
      ...(filterValue.compare.to && {
        // @ts-expect-error TS18048
        compare_date_to: formatIfDate(filterValue.compare.to),
      }),
    }),
  };
}

export function mapDateToQueryString(filterValue: IDateFilter): IKeyValue<string> {
  if (filterValue === null || typeof filterValue !== "object") {
    return {};
  }

  return {
    date: format(filterValue.date, "YYYY-MM-DD"),
  };
}

export function hasAnyFilter(filters?: IFilters): boolean {
  if (!filters) {
    return false;
  }

  return Object.keys(filters).some(key => {
    // @ts-expect-error TS7053
    const filter = filters[key];
    if (Array.isArray(filter)) {
      return filter.length > 0;
    }
    return Boolean(filter);
  });
}
